import moment from 'moment-timezone';
import { MELBOURNE_TIMEZONE } from '../constants/timezone';
import { DATE_WITH_DASH, HOUR } from '../constants/time-formats';

// Used until the patient gives a postcode.
export const DEFAULT_TIMEZONE = MELBOURNE_TIMEZONE;

export const TIMEZONES = [
  'Australia/Melbourne',
  'Australia/Sydney',
  'Australia/Brisbane',
  'Australia/Adelaide',
  'Australia/Perth',
  'Australia/Darwin',
  'Australia/Hobart',
];

// e.g. "Australia/Melbourne (AEST)", with the abbreviation in force on `at` (daylight saving aware).
export const formatTimezone = (timezone: string, at: moment.MomentInput = undefined) => (
  `${timezone} (${moment.tz(at ?? Date.now(), timezone).format('z')})`
);

// Island territories inside the mainland ranges that keep their own clocks.
const ISLAND_TIMEZONES: Record<string, string> = {
  2898: 'Australia/Lord_Howe',
  2899: 'Pacific/Norfolk',
  6798: 'Indian/Christmas',
  6799: 'Indian/Cocos',
};

// Australian postcodes by range. Broken Hill (2880) keeps South Australian time.
export const timezoneForPostcode = (postcode?: string): string | null => {
  if (!postcode || !/^\d{4}$/.test(postcode)) return null;
  if (ISLAND_TIMEZONES[postcode]) return ISLAND_TIMEZONES[postcode];
  const code = Number(postcode);
  if (code >= 800 && code <= 999) return 'Australia/Darwin';
  if (code === 2880) return 'Australia/Adelaide';
  if (code < 3000) return 'Australia/Sydney'; // NSW and ACT, including 02xx
  if (code < 4000 || (code >= 8000 && code < 9000)) return 'Australia/Melbourne';
  if (code < 5000 || code >= 9000) return 'Australia/Brisbane';
  if (code < 6000) return 'Australia/Adelaide';
  if (code < 7000) return 'Australia/Perth';
  if (code < 8000) return 'Australia/Hobart';
  return null;
};

interface WallClockTime {
  date?: string; // YYYY-MM-DD
  startTime?: string; // HH:mm
}

// The same instant shown on another clock, e.g. 9:00 Melbourne -> 6:00 Perth.
export const convertWallClock = ({ date, startTime }: WallClockTime, from: string, to: string): WallClockTime => {
  if (!date || !startTime || from === to) return { date, startTime };
  const converted = moment.tz(`${date} ${startTime}`, `${DATE_WITH_DASH} ${HOUR}`, from).tz(to);
  return { date: converted.format(DATE_WITH_DASH), startTime: converted.format(HOUR) };
};

// ISO 8601 with the right offset for that clock, e.g. 2026-10-02T09:00:00+10:00.
export const toZonedIso = (date: string, time: string, timezone: string) => (
  moment.tz(`${date} ${time}`, `${DATE_WITH_DASH} ${HOUR}`, timezone).format()
);
